import type { Command, CommandError, SyncResponse, User } from "./types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: Partial<CommandError> | null,
  ) {
    super(body?.error ?? `HTTP ${status}`);
  }
  get tag(): string | undefined {
    return this.body?.error_tag;
  }
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/** Network failure (offline, DNS, CORS) as opposed to an HTTP error answer. */
export class NetworkError extends Error {}

const DEFAULT_API = "https://api.letsdoit.deyn.app";

/** API base URL: explicit override (localStorage / build env), then host-based default. */
export function defaultApiBase(): string {
  try {
    const stored = localStorage.getItem("ld.apiBase");
    if (stored) return stored;
  } catch {
    // storage unavailable
  }
  const env = import.meta.env?.VITE_API_URL as string | undefined;
  return env || DEFAULT_API;
}

export class ApiClient {
  constructor(
    readonly baseUrl: string,
    readonly token: string | null,
  ) {}

  private async request<T>(method: string, path: string, body?: unknown, form?: FormData): Promise<T> {
    const headers: Record<string, string> = {};
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    let payload: BodyInit | undefined;
    if (form) payload = form;
    else if (body !== undefined) {
      headers["Content-Type"] = "application/json";
      payload = JSON.stringify(body);
    }
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}/api/v1${path}`, { method, headers, body: payload });
    } catch (err) {
      throw new NetworkError(err instanceof Error ? err.message : String(err));
    }
    const text = await res.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { error: text };
      }
    }
    if (!res.ok) throw new ApiError(res.status, (data as Partial<CommandError>) ?? null);
    return data as T;
  }

  get<T>(path: string, query?: Record<string, string | undefined>): Promise<T> {
    const qs = query
      ? new URLSearchParams(Object.entries(query).filter((e): e is [string, string] => e[1] !== undefined)).toString()
      : "";
    return this.request<T>("GET", qs ? `${path}?${qs}` : path);
  }
  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("POST", path, body ?? {});
  }
  delete<T>(path: string): Promise<T> {
    return this.request<T>("DELETE", path);
  }
  upload<T>(path: string, form: FormData): Promise<T> {
    return this.request<T>("POST", path, undefined, form);
  }

  sync(syncToken: string, commands: Command[] = [], resourceTypes: string[] = ["all"]): Promise<SyncResponse> {
    return this.post<SyncResponse>("/sync", {
      sync_token: syncToken,
      resource_types: resourceTypes,
      ...(commands.length
        ? {
            commands: commands.map(({ type, uuid, temp_id, args }) =>
              temp_id ? { type, uuid, temp_id, args } : { type, uuid, args },
            ),
          }
        : {}),
    });
  }

  login(email: string, password: string): Promise<User> {
    return this.post<User>("/user/login", { email, password, web_session: true, permanent_login: true });
  }
  register(body: {
    email: string;
    password: string;
    full_name?: string;
    invite_code?: string;
    lang?: string;
    timezone?: string;
  }): Promise<User> {
    return this.post<User>("/user/register", { ...body, accept_terms: true, web_session: true, permanent_login: true });
  }
}
